import {useState, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import Alert, {DEFAULT_LABEL_CONFIRM_KEY} from './Alert';
import {AlertPredefinedCallbackWithSSR} from '../constant';
import {Page} from '../constant/page';
import useTypedSelector from '../hook/useTypedSelector';
import {DEFAULT_ANIMATION_DURATION} from '../scss/util/variable';
import {actions as commonActions} from '../state/action';
import {getClientStore} from '../store';
import {PStore} from '../type';
import {
  IPAlertOneConfirm,
  IPAlertTwoConfirm,
  IPAlertCommon,
  ButtonSet,
} from '../type/alert';
import {routeBack, routeTo} from '../util/PageRouter';
import {
  StorageKeyPrefix,
  setLocalStorageBool,
  getLocalStorageBool,
} from '../util/storage';

enum PAlertState {
  Open,
  Closing,
  Closed,
}

// 필수는 아니지만, desc를 string형태로 사용하면 버튼에 적용한 스타일(color, font-size 등)과 안맞을 수 있다.
export default function PAlertComponent() {
  const dispatch = useDispatch();
  const alert = useTypedSelector(state =>
    state.common.pAlerts.length > 0 ? state.common.pAlerts[0] : undefined
  );
  const initialPAlertState = alert ? PAlertState.Open : PAlertState.Closed;
  const [pAlertState, setPAlertState] = useState(initialPAlertState);

  const callbackRef = useRef<Function>();
  useEffect(() => {
    if (alert) {
      callbackRef.current = undefined;
      setPAlertState(PAlertState.Open);
    }
  }, [alert]);
  useEffect(() => {
    if (pAlertState !== PAlertState.Closing) {
      return;
    }
    const timerId = setTimeout(() => {
      setPAlertState(PAlertState.Closed);
      dispatch(commonActions.removeAlert());
      callbackRef.current && callbackRef.current();
    }, DEFAULT_ANIMATION_DURATION);
    return () => clearTimeout(timerId);
  }, [dispatch, pAlertState]);

  if (!alert) {
    return null;
  }
  const onRequestClose = (isDimmedClicked?: boolean) => {
    if (alert.onRequestClose) {
      callbackRef.current = () => alert.onRequestClose?.(isDimmedClicked);
    }
    setPAlertState(PAlertState.Closing);
  };
  const _callback = () => {
    if (alert.callback) {
      callbackRef.current = alert.callback;
      setPAlertState(PAlertState.Closing);
    } else {
      onRequestClose();
    }
  };
  const callbackSecond = () => {
    if (alert.callbackSecond) {
      callbackRef.current = alert.callbackSecond;
    }
    setPAlertState(PAlertState.Closing);
  };
  function getPredefinedCallback(eventType: AlertPredefinedCallbackWithSSR) {
    switch (eventType) {
      case AlertPredefinedCallbackWithSSR.GoToHome:
        return goToHome;
      case AlertPredefinedCallbackWithSSR.GoBack:
        return goBack;
      default:
        return () => {};
    }
  }
  function goToHome() {
    routeTo({page: Page.Home});
    dispatch(commonActions.removeAllAlerts());
  }
  function goBack() {
    routeBack();
    dispatch(commonActions.removeAllAlerts());
  }

  const {predefinedCallback} = alert;
  const callback = predefinedCallback
    ? getPredefinedCallback(predefinedCallback)
    : _callback;

  return (
    <Alert
      {...alert}
      isOpen={pAlertState === PAlertState.Open}
      callback={callback}
      callbackSecond={callbackSecond}
      onRequestClose={onRequestClose}
    />
  );
}

interface IPAlertAsyncCommon {
  // SSR 시에는 반드시 pStore을 입력하자. 그 외에는 입력하지 않아도 좋다.
  pStore?: PStore;
}
type OmitProps = 'onRequestClose' | 'callback' | 'callbackSecond';
interface IPAlertAsyncOneConfirm
  extends Omit<IPAlertOneConfirm, OmitProps>,
    IPAlertAsyncCommon {}
interface IPAlertAsyncTwoConfirm
  extends Omit<IPAlertTwoConfirm, OmitProps>,
    IPAlertAsyncCommon {}
export type IPAlertParam = IPAlertAsyncOneConfirm | IPAlertAsyncTwoConfirm;
interface IPAlertReturnValue {
  isDimmedClicked?: boolean;
  firstClicked?: boolean;
  secondClicked?: boolean;
  okClicked?: boolean;
  closeClicked: boolean;
}
export async function pAlert(
  params: IPAlertParam
): Promise<IPAlertReturnValue> {
  const {buttonSet, pStore = getClientStore()!, desc} = params;
  let okClicked = false;
  let firstClicked = false;
  let secondClicked = false;
  let closeClicked = false;
  let isDimmedClicked = false;
  await new Promise<void>(resolve => {
    pStore.dispatch(
      commonActions.addAlert({
        ...params,
        desc:
          typeof desc === 'string' ? replaceToNewLineFromEscaped(desc) : desc,
        onRequestClose: _isDimmedClicked => {
          if (typeof _isDimmedClicked === 'boolean') {
            isDimmedClicked = !!_isDimmedClicked;
          }
          closeClicked = true;
          resolve();
        },
        callback: () => {
          firstClicked = true;
          resolve();
        },
        callbackSecond: () => {
          secondClicked = true;
          resolve();
        },
      })
    );
  });
  if (buttonSet && TwoConfirmButtonSetList.includes(buttonSet)) {
    return {firstClicked, secondClicked, closeClicked, isDimmedClicked};
  } else {
    okClicked = firstClicked;
    return {okClicked, closeClicked, isDimmedClicked};
  }
}

interface IPAlertWithNotAgain
  extends Pick<IPAlertCommon, 'title' | 'desc' | 'isAliveWhenRouting'> {
  storageKey: string;
  pStore: PStore;
}

export async function pAlertWithNotAgain({
  storageKey,
  title,
  desc,
  isAliveWhenRouting,
  pStore,
}: IPAlertWithNotAgain) {
  const disabled = getLocalStorageBool({
    prefix: StorageKeyPrefix.NeverShowPopupAgain,
    key: storageKey,
  });
  if (!disabled) {
    const {secondClicked} = await pAlert({
      title,
      desc,
      isAliveWhenRouting,
      pStore,
      buttonSet: ButtonSet.TwoConfirm,
      labelConfirm: t => t(DEFAULT_LABEL_CONFIRM_KEY),
      labelSecondConfirm: t => t('doNotSee'),
      isForced: true,
    });
    if (secondClicked) {
      setLocalStorageBool({
        prefix: StorageKeyPrefix.NeverShowPopupAgain,
        key: storageKey,
        value: true,
      });
    }
  }
}

const TwoConfirmButtonSetList = [
  ButtonSet.TwoConfirm,
  ButtonSet.CancelAndTwoConfirm,
];

function replaceToNewLineFromEscaped(source: string) {
  return source.replace('\\n', String.fromCharCode(13, 10));
}
